import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadState, LoadStateInfo, loaded, loading, notStarted, error, failedResponse } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { UserInfo } from "./userSlice";
import { executeUserPermissionInsDel, ExecuteUserPermissionInsDelRequest } from "./userPermissionInsDelSlice";

export interface UsersForPermissionsState {
    state: LoadStateInfo;
    list?: UserInfo[];
    targetId?: string;
    permissions?: string[];
}

const initialState: UsersForPermissionsState = {
    state: notStarted(),
};

export interface RetrieveUsersForPermissionsRequest {
    targetId: string;
    permissions: string[];
}

export const retrieveUsersForPermissions = createAsyncThunk(
    "retrieveUsersForPermissions",
    async (params: RetrieveUsersForPermissionsRequest, thunkAPI) => {
        console.debug("retrieve users for permissions");

        const response = await fetch(`${getBackendUrl()}/api/user/for/permissions`, {
            method: "POST",
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(params)
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    }
);

function notSamePermissions(a?: string[], b?: string[]): boolean {
    if (a == undefined && b == undefined) return false;
    if (a == undefined || b == undefined) return true;
    if (a.length != b.length) return true;
    for (let i = 0; i < a.length; i++) {
        if (a[i] != b[i]) return true;
    }
    return false;
}

export const usersForPermissionsSlice = createSlice({
    name: "usersForPermissionsSlice",
    initialState,
    // The `reducers` field lets us define reducers and generate associated actions
    reducers: {
        clean: (state) => {
            state.state = notStarted();
            state.list = undefined;
        },
        changed: (state, action: PayloadAction<ExecuteUserPermissionInsDelRequest>) => {
            const permissionId = action.payload.permissionId;
            const targetId = action.payload.targetId;
            const userId = action.payload.userId;
            if (state.list) {
                state.list.filter((user) => user.uid == userId).forEach((user) => {
                    if (action.payload.action == "ins") {
                        if (user.permissions[permissionId] == undefined) {
                            user.permissions[permissionId] = [];
                        }
                        user.permissions[permissionId].push(targetId);
                    } else {
                        const pos = user.permissions[permissionId].indexOf(targetId);
                        if (pos >= 0)
                            user.permissions[permissionId].splice(pos, 1);
                    }
                });
            }
        },
        checkLoaded: (state, action: PayloadAction<RetrieveUsersForPermissionsRequest>) => {
            if (state.state.state == LoadState.Loaded &&
                (state.targetId != action.payload.targetId || notSamePermissions(state.permissions, action.payload.permissions))
            ) {
                state.state = notStarted();
                state.list = undefined;
            }
        }
    },
    // The `extraReducers` field lets the slice handle actions defined elsewhere,
    // including actions generated by createAsyncThunk or in other slices.
    extraReducers: (builder) => {
        builder
            .addCase(retrieveUsersForPermissions.pending, (state, action) => {
                state.state = loading();
                state.list = undefined;
                state.targetId = action.meta.arg.targetId;
                state.permissions = action.meta.arg.permissions;
            })
            .addCase(retrieveUsersForPermissions.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.list = action.payload;
                state.list?.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
            })
            .addCase(retrieveUsersForPermissions.rejected, (state, action) => {
                state.state = error(action);
            })
            .addCase(executeUserPermissionInsDel.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                const request = action.meta.arg;
                state.list?.filter((user) => user.uid == request.userId).forEach((user) => {
                    if (request.action == "ins") {
                        if (user.permissions[request.permissionId] == undefined) {
                            user.permissions[request.permissionId] = [];
                        }
                        user.permissions[request.permissionId].push(request.targetId);
                    } else {
                        const pos = user.permissions[request.permissionId].indexOf(request.targetId);
                        if (pos >= 0)
                            user.permissions[request.permissionId].splice(pos, 1);
                    }
                });
            });
    },
});

// export const { } = usersForPermissionsSlice.actions;

export const selectUsersForPermissions = (state: RootState) => state.usersForPermissions;

export default usersForPermissionsSlice.reducer;
